import { Component, inject } from "@angular/core";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { ProductCountryService } from "@domain/product/product-country.service";
import { ProjectFloorplanService } from "@domain/project/project-floorplan.service";
import type { ScopedProject } from "@domain/project/project-guards";
import { ProjectImageService } from "@domain/project/project-image.service";
import { ProjectMigrationService } from "@domain/project/project-migration.service";
import { ProjectService } from "@domain/project/project.service";
import { ToastHelper } from "@utils/toast-helper.service";
import { catchError, of, switchMap, tap } from "rxjs";

@Component({
  selector: "app-project-read-access",
  imports: [RouterModule],
  template: "",
  standalone: true,
  styles: "",
})
export class ProjectReadAccessComponent {
  private projectService = inject(ProjectService);
  private productCountryService = inject(ProductCountryService);
  private projectImageService = inject(ProjectImageService);
  private projectFloorplanService = inject(ProjectFloorplanService);
  private projectMigrationService = inject(ProjectMigrationService);
  private router = inject(Router);
  private toastHelper = inject(ToastHelper);

  constructor() {
    const activatedRoute = inject(ActivatedRoute);
    // projectId query param = cloudId of the shared project in the storage account
    const cloudId = activatedRoute.snapshot.queryParams["projectId"];

    this.projectService
      .clearAll()
      .pipe(
        switchMap(() => this.projectService.loadReadAccessProjectFromCloud(cloudId)),
        tap((project) => this.productCountryService.selectCountry(project.country)),
        switchMap((project) => this.projectService.createProject(project)),
        switchMap((project) =>
          this.projectImageService.getProjectImages(cloudId, project.id).pipe(
            catchError(() => of(null)),
            switchMap(() =>
              this.projectFloorplanService
                .getProjectFloorplans({ cloudId, id: project.id } as ScopedProject)
                .pipe(catchError(() => of(null))),
            ),
            switchMap(() => this.projectMigrationService.runMigrations(project, (p) => this.projectService.update(p))),
          ),
        ),
      )
      .subscribe({
        next: (project) => {
          this.projectService.setReadAccessProjectId(project.id);
          this.router.navigate([`/projects/${project.id}`]);
        },
        error: () => {
          this.toastHelper.error(
            $localize`:@@app.errorMessage.loadingReadOnlyProject.text:Der Link ist ungültig. Das Projekt konnte nicht gefunden werden.`,
          );
        },
      });
  }
}
